import { ApolloError, gql } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Grid,
  GridItem,
  Heading,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { ConfirmationModal } from '~components/ConfirmModal';
import { ContactSupport, ErrorDetail } from '~components/ContactSupport';
import { OrganisationDetailsForm } from '~components/landing/organisationDetailsForm';
import { PersonalDetailsForm } from '~components/landing/personalDetailsForm';
import { CreateOrgDataSchema, CreateOrgDataType } from '~components/landing/schema';
import { PageContent } from '~components/ui/PageContent';
import { useOrganizationSignupMutation } from './__generated__/Landing.graphql';

const formWidth = '85%';
const ERROR_COUNT_THRESHOLD = 5;
const ERROR_DETAILS = 'ERROR_DETAILS';
const WAVE_REDIRECT_URL =
  process.env.REACT_APP_POST_SIGNUP_REDIRECT_URL || 'https://wave.ppds.com/login'; // for dev or prod env
const PROSTORE_REDIRECT_URL =
  process.env.REACT_APP_POST_SIGNUP_PROSTORE_REDIRECT_URL ||
  'https://prostore.ppds.com/reseller/companies';

export function slugify(input: string) {
  return input
    .toString() // Ensure input is a string
    .trim() // Remove leading/trailing whitespace
    .toLowerCase() // Convert to lowercase
    .normalize('NFD') // Normalize to decompose diacritics (e.g., "é" becomes "e")
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^a-z0-9\s-]/g, '') // Remove invalid characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Remove multiple hyphens
}

const getErrorCount = () => getErrorDetails().length;
const setErrorDetails = (errorDetails: ErrorDetail[]) =>
  localStorage.setItem(ERROR_DETAILS, JSON.stringify(errorDetails));
const addErrorDetail = (errorMessage: string, fieldValues: ErrorDetail['fieldValues']) => {
  const errorDetails = getErrorDetails();
  errorDetails.push({
    timestamp: new Date().toISOString(),
    errorMessage,
    fieldValues,
  });
  setErrorDetails(errorDetails);
  return errorDetails;
};
const getErrorDetails = (): ErrorDetail[] => {
  return JSON.parse(localStorage.getItem(ERROR_DETAILS) || '[]');
};

const isErrorThresholdExceeded = () => getErrorCount() > ERROR_COUNT_THRESHOLD;

export function LandingPage() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const resellerId = searchParams.get('uuid');
  const prefilledEmail = searchParams.get('email');
  const prefilledFirstName = searchParams.get('givenName');
  const prefilledLastName = searchParams.get('familyName');

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => setErrorDetails([]), []);

  const [organisationSignup] = useOrganizationSignupMutation();
  const formControl = useForm<CreateOrgDataType>({
    resolver: zodResolver(CreateOrgDataSchema),
    defaultValues: {
      countryCode: '',
      email: prefilledEmail ?? '',
      firstName: prefilledFirstName ?? '',
      lastName: prefilledLastName ?? '',
    },
    mode: 'onTouched',
  });

  const createError = (message: string) => ({
    type: 'manual',
    message,
  });

  const handleSubmitSuccess = () => {
    toast({
      title: 'Success',
      description: 'Organisation created successfully.',
      duration: 5000,
      isClosable: true,
      status: 'success',
    });
    setErrorDetails([]);
  };

  const handleSubmitError = (err: unknown) => {
    let msg = 'Unexpected error occured while creating organisation. Please try again later.';
    if (err instanceof ApolloError) {
      switch (err.message) {
        case 'ORGANIZATION_NAME_DUPLICATE': {
          msg = 'Organisation with given name already exists.';
          formControl.setError('organisationDetail', createError(msg));
          addErrorDetail(
            `${msg}. Org name used: ${formControl.getValues().organisationDetail.orgName}.`,
            formControl.getValues(),
          );
          break;
        }
        case 'ORGANIZATION_HANDLE_DUPLICATE': {
          msg = 'Organisation with given handle already exists.';
          formControl.setError('organisationHandle', createError(msg));
          addErrorDetail(
            `${msg}. Org handle used: ${formControl.getValues().organisationHandle}.`,
            formControl.getValues(),
          );
          break;
        }
        case 'USER_COULD_NOT_BE_CREATED': {
          msg = 'This user already has an organisation.';
          formControl.setError('email', createError(msg));
          addErrorDetail(
            `${msg}. Email used: ${formControl.getValues().email}.`,
            formControl.getValues(),
          );
          break;
        }
        case 'RESELLER_NOT_EXISTS': {
          msg = "The reseller with given uuid doesn't exist.";
          addErrorDetail(`${msg}. Uuid used: ${resellerId}.`, formControl.getValues());
          break;
        }
        default:
          addErrorDetail(`Unexpected error occured. Error detail: ${err}`, formControl.getValues());
      }
    } else {
      addErrorDetail(`Unexpected error occured. Error detail: ${err}`, formControl.getValues());
    }

    if (!resellerId) setErrorDetails([]);

    if (isErrorThresholdExceeded()) {
      console.log('Error threshold exceeded.');
      toast({
        isClosable: true,
        duration: null,
        status: 'error',
        containerStyle: {
          maxWidth: '700px',
          width: 'auto',
        },
        render: ({ onClose }) => {
          return (
            <Alert status="error" variant={'solid'} maxWidth={'700px'}>
              <AlertIcon />
              <Box>
                <AlertTitle>Error while creating organisation</AlertTitle>
                <AlertDescription>{msg}</AlertDescription>
              </Box>
              <Box ml="10">
                <ContactSupport
                  errorDetails={getErrorDetails()}
                  resellerId={resellerId as string}
                  onClose={onClose}
                />
              </Box>

              <CloseButton
                alignSelf="flex-start"
                position="relative"
                right={-1}
                top={-1}
                onClick={onClose}
              />
            </Alert>
          );
        },
      });
      return;
    }

    toast({
      title: 'Error while creating organisation.',
      description: msg,
      isClosable: true,
      duration: 5000,
      status: 'error',
    });
  };

  const onSubmit = async (data: CreateOrgDataType) => {
    try {
      await organisationSignup({
        variables: {
          input: {
            name: data.organisationDetail.orgName,
            handle: data.organisationHandle,
            ownerEmail: data.email,
            ownerGivenName: data.firstName,
            ownerFamilyName: data.lastName,
            countryCode: data.countryCode,
            dunsId: data.organisationDetail.dunsId,
            resellerId,
          },
        },
      });
      handleSubmitSuccess();
    } catch (err) {
      handleSubmitError(err);
      formControl.setError('root', createError('Unexpected error.'));
    }
  };

  const orgDetail = formControl.watch('organisationDetail');
  const createOrgCompleted =
    formControl.formState.isSubmitted && formControl.formState.isSubmitSuccessful;

  useEffect(() => {
    if (!orgDetail || !orgDetail.orgName) return;

    formControl.setValue('organisationHandle', slugify(orgDetail.orgName));
  }, [formControl, orgDetail]);

  return (
    <PageContent>
      <Heading as={'h1'} size={'xl'} mt={6}>
        Add new Organisation
      </Heading>

      {createOrgCompleted && (
        <Grid w={'100%'} templateColumns="repeat(8, 1fr)" gap={1} mt={12}>
          {resellerId
            ? getFormCompletedComponent(
                'The organisation is created successfully and the invitation is sent to the user.',
                'Go to Prostore',
                PROSTORE_REDIRECT_URL,
              )
            : getFormCompletedComponent(
                'The organisation is created successfully and the email invitation is sent. Please validate with the link provided in the email.',
                'Login',
                WAVE_REDIRECT_URL,
              )}
        </Grid>
      )}

      <form onSubmit={formControl.handleSubmit(onSubmit)}>
        <Text fontSize="lg" width={'100%'} mt={10}>
          Personal Details
        </Text>
        <Box w={formWidth}>
          <PersonalDetailsForm
            form={formControl}
            readOnlyFields={{
              email: Boolean(prefilledEmail),
              firstName: Boolean(prefilledFirstName),
              lastName: Boolean(prefilledLastName),
            }}
          />
        </Box>
        <Text fontSize="lg" width={'100%'} mt={8}>
          Organisation Details
        </Text>
        <Box w={formWidth}>
          <OrganisationDetailsForm form={formControl} />
        </Box>
        {!createOrgCompleted && (
          <Grid w={'100%'} templateColumns="repeat(4, 1fr)" gap={1} mt={6}>
            <GridItem colSpan={3} />
            <GridItem p={0} m={0} justifyItems={'end'}>
              <HStack spacing={8}>
                <Button
                  h={12}
                  bg={'white'}
                  borderRadius={'4px'}
                  border={'1px solid #BCCCDC'}
                  p={'10px 24px 10px 24px'}
                  shadow={0}
                  onClick={onOpen}
                  isDisabled={formControl.formState.isSubmitting}
                >
                  Reset Form
                </Button>
                <Button
                  h={12}
                  bg={formControl.formState.isSubmitting ? '#BCCCDC' : 'blue.700'}
                  borderRadius={'4px'}
                  border={'1px solid #BCCCDC'}
                  p={'10px 24px 10px 24px'}
                  shadow={0}
                  type="submit"
                  isDisabled={formControl.formState.isSubmitting}
                  color={formControl.formState.isSubmitting ? 'black' : 'white'}
                >
                  <span>Submit</span>
                  {formControl.formState.isSubmitting && <Spinner size={'sm'} ml={2} />}
                </Button>
              </HStack>
            </GridItem>
          </Grid>
        )}
      </form>
      <ConfirmationModal
        onCancel={onClose}
        onConfirm={() => {
          formControl.reset();
          onClose();
        }}
        onClose={onClose}
        isOpen={isOpen}
      />
    </PageContent>
  );
}

const getFormCompletedComponent = (
  displayText: string,
  buttonLabel: string,
  redirectUrl: string,
) => {
  return (
    <>
      <GridItem colSpan={7}>
        <Text fontSize={'2xl'}>{displayText}</Text>
      </GridItem>
      <GridItem textAlign={'right'} w={'100%'}>
        <Button
          justifySelf={'end'}
          bg={'#0F5291'}
          color={'white'}
          onClick={() => window.open(redirectUrl, '_blank')}
        >
          {buttonLabel}
        </Button>
      </GridItem>
    </>
  );
};

LandingPage.graphql = {
  mutations: {
    organisationSignup: gql`
      mutation OrganizationSignup($input: OrganizationSignupInput!) {
        organizationSignup(input: $input) {
          id
          name
          createdAt
          displayCount
          handle
          countryCode
          enableMfa
          owner {
            id
            email
            organizationId
            lastLogin
          }
          proStore {
            id
            createdAt
            developerAccess
          }
        }
      }
    `,
  },
};
